<template>
  <div data-locator="q-and-a-page">
    <v-container
      :fluid="true"
      class="layout-spacing"
    >
      <template v-if="noData">
        <QuestionAnswerTopBar
          :mnemo="roomMnemo"
          v-model:search-query="searchQuery"
          is-empty-state
          disabled
          @open-import-excel-dialog="importDialogIsOpen = true"
        />

        <app-empty-state
          class="empty-state-container"
          module="question-answer"
          size="md"
          :translation-key-prefix="TRANSLATION_KEY_PREFIX"
          :force-default-translation="isCurrentUserPm || currentUserRights.canAddQuestions"
        >
          <i18n-t
            v-if="isCurrentUserPm"
            :keypath="`${TRANSLATION_KEY_PREFIX}.emptyState.settingsText`"
            tag="span"
            scope="global"
            data-locator="empty-state-settings"
          >
            <template #settings>
              <router-link
                :to="roomSettingsRoute"
                class="qa-empty-state-settings__link"
                data-locator="empty-state-settings-link"
              >
                {{ $t(`${TRANSLATION_KEY_PREFIX}.emptyState.settingsLinkLabel`) }}
              </router-link>
            </template>
          </i18n-t>
        </app-empty-state>
      </template>

      <template v-else-if="initialDataLoading">
        <AppDataTableSkeletonLoader class="mt-8" />
      </template>

      <template v-else-if="dataReady">
        <QuestionAnswerListHeader />

        <QuestionAnswerTopBar
          :mnemo="roomMnemo"
          v-model:search-query="searchQuery"
          class="my-8"
          @open-import-excel-dialog="importDialogIsOpen = true"
          @open-settings="goToRoomSettings({ goTo: '#hideQaAuthorsCard' })"
        />

        <QuestionAnswerDataTable
          v-model:search-query="searchQuery"
        />
      </template>

      <QuestionAnswerFiltersPane :mnemo="roomMnemo" />
    </v-container>

    <QuestionAnswerImportDialog
      v-if="importDialogIsOpen"
      :mnemo="roomMnemo"
      @close="importDialogIsOpen = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
import { getRoomRouteType } from '@/common/utils/rooms'
import { toast } from '@/common/utils/toast'
import QuestionAnswerDataTable from '@/project/question-answer/QuestionAnswerDataTable.vue'
import QuestionAnswerFiltersPane from '@/project/question-answer/QuestionAnswerFiltersPane.vue'
import QuestionAnswerImportDialog from '@/project/question-answer/QuestionAnswerImportDialog.vue'
import QuestionAnswerListHeader from '@/project/question-answer/QuestionAnswerListHeader.vue'
import QuestionAnswerTopBar from '@/project/question-answer/QuestionAnswerTopBar.vue'
import { ROOM_MEMBERS_ROUTE_NAME, ROOM_SETTINGS_ROUTE_NAME } from '@/router/routerData'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'

const TRANSLATION_KEY_PREFIX = 'project.question-answer.QuestionAnswerList'

export default {
  name: 'QuestionAnswerList',
  components: {
    AppDataTableSkeletonLoader,
    QuestionAnswerDataTable,
    QuestionAnswerFiltersPane,
    QuestionAnswerImportDialog,
    QuestionAnswerListHeader,
    QuestionAnswerTopBar,
  },
  data () {
    return {
      searchQuery: '',
      importDialogIsOpen: false,
      TRANSLATION_KEY_PREFIX,
    }
  },
  head () {
    return {
      title: this.$t('project.views.QuestionAnswerList.title'),
    }
  },
  computed: {
    ...mapState('questionAnswer', ['questions', 'questionsPending']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo', 'currentUserRights']),
    noData () {
      return this.questions && this.questions.questions.length === 0
    },
    initialDataLoading () {
      return this.questionsPending && !this.questions
    },
    dataReady () {
      return this.questions && !this.initialDataLoading
    },
    roomSettingsRoute () {
      return {
        name: ROOM_SETTINGS_ROUTE_NAME,
        params: {
          roomType: getRoomRouteType(this.currentRoom),
          mnemo: this.roomMnemo,
        },
      }
    },
  },
  created () {
    if (!this.currentRoom.isDataroom) {
      this.$router.replace({
        name: ROOM_MEMBERS_ROUTE_NAME,
        params: { roomType: getRoomRouteType(this.currentRoom) },
      })
    }
    this.prepareGetQuestions()
  },
  methods: {
    ...mapActions('questionAnswer', [QuestionAnswerActionTypes.GET_QUESTIONS]),
    async prepareGetQuestions () {
      try {
        await this.GET_QUESTIONS(this.roomMnemo)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.views.QuestionAnswerList.getQuestionsError'))
      }
    },
    goToRoomSettings ({ goTo } = {}) {
      this.$router.push({
        ...this.roomSettingsRoute,
        ...(goTo ? { state: { goTo } } : {}),
      })
    },
  },
}
</script>

<style scoped lang="scss">
.empty-state-container {
  margin-top: 88px;
}

.qa-empty-state-settings__link {
  color: rgb(var(--v-theme-text-link));
}
</style>
